import { Component, inject, signal } from "@angular/core"
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from "@angular/forms"
import { ActivatedRoute, Router, RouterModule } from "@angular/router"

import { Button } from "primeng/button"
import { InputText } from "primeng/inputtext"
import { Password } from "primeng/password"

import { AuthPasswordApi } from "@logick/core-api"

type PageStep = "token" | "new-password" | "success"

function passwordMatchValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const password = group.get("password")?.value
    const confirm = group.get("passwordConfirm")?.value
    return password && confirm && password !== confirm ? { mismatch: true } : null
  }
}

function passwordComplexityValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value ?? ""
    const errors: ValidationErrors = {}
    if (value.length < 8 || value.length > 128) errors["length"] = true
    if (!/[0-9]/.test(value)) errors["digit"] = true
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) errors["special"] = true
    if (!/[a-z]/.test(value)) errors["lowercase"] = true
    if (!/[A-Z]/.test(value)) errors["uppercase"] = true
    return Object.keys(errors).length ? errors : null
  }
}

@Component({
  selector: "app-password-validation-page",
  imports: [ReactiveFormsModule, RouterModule, InputText, Password, Button],
  templateUrl: "./password-validation.page.html",
})
export class PasswordValidationPage {

  private fb = inject(FormBuilder)
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  private authPasswordApi = inject(AuthPasswordApi)

  step = signal<PageStep>("token")

  tokenForm: FormGroup = this.fb.group({
    token: ["", [Validators.required]],
  })

  passwordForm: FormGroup = this.fb.group(
    {
      password: ["", [Validators.required, passwordComplexityValidator()]],
      passwordConfirm: ["", [Validators.required]],
    },
    { validators: passwordMatchValidator() }
  )

  passwordRules = signal(this.buildPasswordRules(""))

  constructor() {
    const token = this.route.snapshot.queryParamMap.get("token")
    if (token) {
      this.tokenForm.get("token")?.setValue(token)
      this.step.set("new-password")
    }

    this.passwordForm.get("password")?.valueChanges.subscribe((value: string) => {
      this.passwordRules.set(this.buildPasswordRules(value ?? ""))
    })
  }

  private buildPasswordRules(value: string) {
    return [
      { label: "Entre 8 et 128 caractères", valid: value.length >= 8 && value.length <= 128 },
      { label: "Au moins une minuscule", valid: /[a-z]/.test(value) },
      { label: "Au moins une majuscule", valid: /[A-Z]/.test(value) },
      { label: "Au moins un chiffre", valid: /[0-9]/.test(value) },
      { label: "Au moins un caractère spécial", valid: /[^a-zA-Z0-9]/.test(value) },
    ]
  }

  loading = signal(false)
  apiError = signal<string | null>(null)

  submitToken() {
    if (!this.tokenForm.valid) {
      return
    }
    this.step.set("new-password")
  }

  submitPassword() {
    if (this.loading() || !this.passwordForm.valid) {
      return
    }

    this.loading.set(true)
    this.apiError.set(null)

    this.authPasswordApi.resetPasswordByToken({
      token: this.tokenForm.get("token")?.value,
      password: this.passwordForm.get("password")?.value,
    }).subscribe({
      next: () => {
        this.router.navigate(["/login"])
      },
      error: () => {
        this.loading.set(false)
        this.apiError.set("Code invalide ou expiré. Veuillez recommencer.")
        this.step.set("token")
        this.tokenForm.reset()
        this.router.navigate([], { queryParams: {}, replaceUrl: true })
      },
    })
  }
}
